import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ListRow, tripHueColor, vars } from "@pack-it-up/ui-web";
import { byOrder, type CategoryId, type FractionalIndex, type ItemId, type ParticipantId, type TemplateId, type TripColor, type UserId } from "@pack-it-up/shared";
import { createInlineEdit, type Neighbors, type ShortcutRegistry } from "@pack-it-up/viewmodel";
import { allTags, isCategoryCollapsed, isItemComplete, participantsOf } from "@pack-it-up/core";
import type { Assignment, Category, CompletionField, ItemDisplay, PackingItem, ParticipantContext, TemplateDisplay } from "@pack-it-up/core";
import { DndContext, DragOverlay, closestCenter, type DragStartEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { peopleOf } from "./people.js";
import { Sortable } from "../../features/dnd/Sortable.js";
import { useDndSensors } from "../../features/dnd/sensors.js";
import { makeCategoryDragEnd, makeRowDragEnd } from "../../features/dnd/dropHandlers.js";
import { DragDisabledHint } from "../../features/dnd/DragDisabledHint.js";
import { DragPreview } from "../../features/dnd/DragPreview.js";
import { AddRow } from "./AddRow.js";
import { AssignmentChips } from "./AssignmentChips.js";
import { CategorySection } from "./CategorySection.js";
import { CompletionControl } from "./CompletionControl.js";
import { ExpandedCompletionControl } from "./ExpandedCompletionControl.js";
import { ExpandedParticipantPills } from "./ExpandedParticipantPills.js";
import type { ItemComposerDraft } from "./ItemComposer.js";
import { ItemEditForm, type ItemPatchDraft } from "./RowEditForm.js";
import { RowName } from "./RowName.js";
import { RowTags } from "./RowTags.js";
import { DragHandle } from "./DragHandle.js";
import { SyncStatusBadge } from "./SyncStatusBadge.js";
import { SyncCategoryTick, SyncRowTick, type SyncListMode } from "./syncMode.js";
import type { ListActions } from "./ListActions.js";

// Qty badge shown in the item row trailing area. x1 is the assumed default — only
// worth a badge when the quantity says otherwise.
const QtyBadge = ({ qty }: { qty: number }): ReactNode => {
  if (qty === 1) return null;
  return (
    <span
      style={{
        fontSize: "12px",
        color: vars.color.textMuted,
        background: vars.color.surfaceSunken,
        padding: "2px 7px",
        borderRadius: vars.radius.xs,
        flexShrink: 0,
      }}
    >
      ×{qty}
    </span>
  );
};

export const PackingList = ({
  categories,
  items,
  list,
  actor,
  canEdit,
  sort,
  registry,
  stagingEnabled = false,
  tagPalette,
  templateDisplay = "tag",
  resolveTemplateColor,
  syncMode,
  field = "completions",
  renderCompletion,
  itemDisplay = "condensed",
  filterParticipant,
  onToggle,
  onToggleAllParticipants,
  onCheckAll,
  onUncheckAll,
  onToggleCollapse,
  onAddItem,
  onAddCategory,
  onUpdateCategory,
  onDeleteCategory,
  onUpdateItem,
  onSetAssignment,
  onResetToImport,
  onDeleteItem,
  onMoveItem,
  onReorderCategory,
}: {
  categories: Category[];
  items: PackingItem[];
  // The trip or template these rows belong to (issue #188): only its participants and
  // proxy-completion setting are read, so one list component serves both.
  list: ParticipantContext;
  // Absent for a link-grant viewer (Doc 08 §4a) — no "me" to check off; rows still
  // render, the checkbox reads as an aggregate and the expanded self-view is unavailable.
  actor?: UserId;
  sort: "manual" | "alphabetical" | "dueDate";
  registry: ShortcutRegistry;
  // T27: when true, rows show the 3-way staged/packed cycle and categories get a
  // "check all" affordance.
  stagingEnabled?: boolean;
  // Tags in use on this trip/template (T29) — offered in the add/edit composers.
  tagPalette?: string[];
  // Trip setting: a row's source-template auto-tag renders as a tag chip ("tag",
  // default) or as a color bar on the row's right edge ("highlight"). Omit for "tag".
  templateDisplay?: TemplateDisplay;
  // Resolves a template's color for the highlight bar — the template's own color if
  // set, else the same deterministic hash trip/sidebar cards fall back to. Required
  // when templateDisplay is "highlight"; unused otherwise.
  resolveTemplateColor?: (templateId: TemplateId) => TripColor;
  // Which per-participant map drives the counts, the row-complete styling and the
  // assignment chips (issue #188): "completions" on a trip, "staging" in a template,
  // where a checkbox only ever stages.
  field?: CompletionField;
  // Replaces the leading checkbox. A template supplies its own control — there is no
  // "me" to check a row off there, so the box acts on the row's travelers instead.
  renderCompletion?: (item: PackingItem) => ReactNode;
  // T28: when present, the list is in sync mode — the page has already passed
  // `canEdit: false`, so inline edit, drag-and-drop and the add rows are gone; ticks
  // take the leading slot and completion toggling is out of reach.
  syncMode?: SyncListMode;
  // Issue #203: self-scoped viewer preference — "condensed" (default) keeps today's
  // small avatar-dot chips; "expanded" swaps them for a full pill row per participant
  // and turns the leading checkbox into "check/uncheck everyone". Only applies to real
  // trip rows (renderCompletion/syncMode both take priority — a template or sync row
  // has no "me" to hold this preference).
  itemDisplay?: ItemDisplay;
  // Issue #203 follow-up: the toolbar's assignee filter, when narrowed to one person.
  // In expanded mode this hides the per-participant pills (redundant with the filter)
  // and scopes the leading checkbox to that one participant.
  filterParticipant?: ParticipantId;
  onToggle: (itemId: string, target: ParticipantId, targetIsUser: boolean) => void;
  // Required when itemDisplay is "expanded"; unused in condensed mode.
  onToggleAllParticipants?: (itemId: string) => void;
  // "More" menu bulk actions (Check all / Uncheck all). Omit either to hide that item.
  onCheckAll?: (categoryId: CategoryId) => void;
  onUncheckAll?: (categoryId: CategoryId) => void;
  onAddItem: (categoryId: CategoryId, draft: ItemComposerDraft) => void;
  onUpdateItem: (itemId: ItemId, patch: ItemPatchDraft) => void;
  onSetAssignment: (itemId: string, assignment: Assignment) => void;
  onResetToImport: (itemId: ItemId) => void;
  // Trash icon on the edit form (top-right of the name row) — omit to hide it (sync
  // mode's read-only rows never get canEdit anyway, but this keeps callers explicit).
  onDeleteItem?: (itemId: ItemId) => void;
  onMoveItem: (rowId: string, toCategoryId: CategoryId, neighbors: Neighbors) => void;
  onReorderCategory: (categoryId: CategoryId, order: FractionalIndex) => void;
} & ListActions): ReactNode => {
  const edit = useMemo(() => createInlineEdit(), []);
  const editingId = useStoreValue(edit, (s) => (s.mode === "editing" ? s.targetId : null));
  const participants = participantsOf(list);
  const people = useMemo(() => peopleOf(list), [list]);
  const close = (): void => void edit.getState().cancel();
  const isManual = sort === "manual";
  const sortedCategories = [...categories].sort(byOrder);
  // A template row (renderCompletion) or a sync-mode row (ticks, not checkboxes) has no
  // "me" for this self-scoped preference to apply to — always condensed there.
  const expanded = itemDisplay === "expanded" && renderCompletion === undefined && syncMode === undefined;

  // Esc cancels the open row edit — the same inline-edit command the form's own Esc
  // path calls (Doc 06 §8). Registered here because the edit machine is list-local.
  // Cancel with no edit open is a no-op, so double-handling with the form is harmless.
  useEffect(() => registry.register("editor", { escape: () => edit.getState().cancel() }), [registry, edit]);

  // Drop handlers (features/dnd): a drag gesture resolves to exactly one store command.
  const catSensors = useDndSensors();
  const itemSensors = useDndSensors();
  const handleCatDragEnd = makeCategoryDragEnd(sortedCategories, onReorderCategory);
  const handleItemDragEnd = makeRowDragEnd(items, sortedCategories, onMoveItem);
  // The dragged row itself fades in place inside its origin category's SortableContext —
  // crossing into another category buries it behind that category's own opaque card, so
  // a DragOverlay ghost (portal-rendered above everything) is what's actually visible.
  const [draggingItem, setDraggingItem] = useState<PackingItem | null>(null);
  const handleItemDragStart = (event: DragStartEvent): void =>
    setDraggingItem(items.find((i) => i.id === event.active.id) ?? null);

  return (
    <>
      {!isManual && canEdit && <DragDisabledHint />}
      <DndContext sensors={catSensors} collisionDetection={closestCenter} onDragEnd={handleCatDragEnd}>
        <SortableContext items={sortedCategories.map((c) => c.id)} strategy={verticalListSortingStrategy} disabled={!isManual}>
          <DndContext
            sensors={itemSensors}
            collisionDetection={closestCenter}
            onDragStart={handleItemDragStart}
            onDragEnd={(event) => {
              handleItemDragEnd(event);
              setDraggingItem(null);
            }}
            onDragCancel={() => setDraggingItem(null)}
          >
            {sortedCategories.map((c, catIdx) => {
              // `items` arrive in view order (manual byOrder baseline + the toolbar's
              // sort/completedToBottom, applied in visibleItems) — don't re-sort here.
              const catItems = items.filter((i) => i.categoryId === c.id);
              // Count label: "N / M" with "✓" suffix when all complete (kit .cat-count).
              const fullyDone = catItems.filter((i) => isItemComplete(i, participants, field)).length;
              const countLabel = catItems.length === 0
                ? "0 / 0"
                : `${fullyDone} / ${catItems.length}${fullyDone === catItems.length ? " ✓" : ""}`;
              return (
                <Sortable key={c.id} id={c.id} disabled={!isManual || !canEdit}>
                  {(catHandleProps) => (
                    <SortableContext
                      id={c.id}
                      items={catItems.map((i) => i.id)}
                      strategy={verticalListSortingStrategy}
                      disabled={!isManual}
                    >
                      <CategorySection
                        name={c.name}
                        count={countLabel}
                        collapsed={isCategoryCollapsed(c, actor)}
                        canEdit={canEdit}
                        onToggleCollapse={() => onToggleCollapse(c.id)}
                        onAdd={(name) => onAddItem(c.id, { name, quantity: 1, assignment: { kind: "everyone" }, notes: "", dueDate: "", tags: [] })}
                        people={people}
                        {...(tagPalette !== undefined ? { tagPalette } : {})}
                        onAddItem={(draft) => onAddItem(c.id, draft)}
                        onRename={(name) => onUpdateCategory(c.id, { name })}
                        onDelete={() => onDeleteCategory(c)}
                        {...(canEdit && catItems.length > 0 && onCheckAll !== undefined ? { onCheckAll: () => onCheckAll(c.id) } : {})}
                        {...(canEdit && catItems.length > 0 && onUncheckAll !== undefined ? { onUncheckAll: () => onUncheckAll(c.id) } : {})}
                        {...(isManual && canEdit ? { dragHandleProps: catHandleProps } : {})}
                        {...(catIdx === 0 ? { addShortcut: registry } : {})}
                        {...(syncMode !== undefined
                          ? { leading: <SyncCategoryTick mode={syncMode} categoryId={c.id} name={c.name} /> }
                          : {})}
                      >
                        {catItems.map((i) => {
                          const isEditing = editingId === i.id;
                          const isComplete = isItemComplete(i, participants, field);
                          const syncEntry = syncMode?.entryFor("items", i.id);
                          // Issue: "Show template tags as: highlight" swaps the source-template
                          // auto-tag for a color bar instead — own tags still show as chips.
                          const highlightSource = templateDisplay === "highlight" ? i.provenance?.templateSources[0] : undefined;
                          const accentColor =
                            highlightSource !== undefined && resolveTemplateColor !== undefined
                              ? tripHueColor[resolveTemplateColor(highlightSource.templateId)]
                              : undefined;
                          const displayTags = templateDisplay === "highlight" ? (i.tags ?? []) : allTags(i);
                          return (
                            <Sortable key={i.id} id={i.id} disabled={!isManual || !canEdit}>
                              {(handleProps) => (
                                <ListRow
                                  aria-label={i.name}
                                  editing={isEditing}
                                  completed={isComplete}
                                  {...(syncMode === undefined && accentColor !== undefined ? { accentColor } : {})}
                                  {...(canEdit && !isEditing ? { onClick: () => edit.getState().beginEdit(i.id, i.name) } : {})}
                                  leading={
                                    syncMode !== undefined ? (
                                      syncEntry !== undefined ? <SyncRowTick mode={syncMode} entry={syncEntry} name={i.name} /> : undefined
                                    ) : isEditing ? undefined : (
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }} onClick={(e) => e.stopPropagation()}>
                                        {isManual && canEdit ? (
                                          <DragHandle listeners={handleProps.listeners} attributes={handleProps.attributes} />
                                        ) : null}
                                        {renderCompletion !== undefined ? (
                                          renderCompletion(i)
                                        ) : expanded && actor !== undefined && i.assignment.kind !== "shared" ? (
                                          <ExpandedCompletionControl
                                            item={i}
                                            list={list}
                                            actor={actor}
                                            canEdit={canEdit}
                                            field={field}
                                            stagingEnabled={stagingEnabled}
                                            {...(filterParticipant !== undefined ? { filterParticipant } : {})}
                                            onAdvanceAll={() => onToggleAllParticipants?.(i.id)}
                                            onToggle={(target, isUser) => onToggle(i.id, target, isUser)}
                                          />
                                        ) : (
                                          <CompletionControl
                                            item={i}
                                            list={list}
                                            canEdit={canEdit}
                                            stagingEnabled={stagingEnabled}
                                            {...(actor !== undefined ? { actor } : {})}
                                            onToggle={(target, isUser) => onToggle(i.id, target, isUser)}
                                          />
                                        )}
                                      </span>
                                    )
                                  }
                                  trailing={
                                    syncMode !== undefined ? (
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                        <QtyBadge qty={i.quantity} />
                                        {syncEntry !== undefined && <SyncStatusBadge entry={syncEntry} />}
                                      </span>
                                    ) : isEditing ? undefined : (
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                        <QtyBadge qty={i.quantity} />
                                        <RowTags tags={displayTags} />
                                        {(!expanded || i.assignment.kind === "shared") && (
                                          <AssignmentChips
                                            item={i}
                                            list={list}
                                            field={field}
                                            pastTense={field === "staging" ? "staged" : "packed"}
                                            canEdit={canEdit}
                                            stagingEnabled={stagingEnabled}
                                            onToggle={(target, isUser) => onToggle(i.id, target, isUser)}
                                          />
                                        )}
                                      </span>
                                    )
                                  }
                                >
                                  {isEditing ? (
                                    <ItemEditForm
                                      item={i}
                                      people={people}
                                      canEdit={canEdit}
                                      {...(tagPalette !== undefined ? { tagPalette } : {})}
                                      onCommit={(patch) => {
                                        if (Object.keys(patch).length > 0) onUpdateItem(i.id, patch);
                                        close();
                                      }}
                                      onCancel={close}
                                      onSetAssignment={(asg) => onSetAssignment(i.id, asg)}
                                      {...(i.provenance?.createdByImport
                                        ? {
                                            onResetToTemplate: () => {
                                              onResetToImport(i.id);
                                              close();
                                            },
                                          }
                                        : {})}
                                      {...(onDeleteItem !== undefined
                                        ? {
                                            onDelete: () => {
                                              onDeleteItem(i.id);
                                              close();
                                            },
                                          }
                                        : {})}
                                    />
                                  ) : (
                                    <>
                                      <RowName name={i.name} description={i.description} />
                                      {expanded && actor !== undefined && filterParticipant === undefined && (
                                        <ExpandedParticipantPills
                                          item={i}
                                          list={list}
                                          actor={actor}
                                          field={field}
                                          pastTense={field === "staging" ? "staged" : "packed"}
                                          canEdit={canEdit}
                                          stagingEnabled={stagingEnabled}
                                          onToggle={(target, isUser) => onToggle(i.id, target, isUser)}
                                        />
                                      )}
                                    </>
                                  )}
                                </ListRow>
                              )}
                            </Sortable>
                          );
                        })}
                      </CategorySection>
                    </SortableContext>
                  )}
                </Sortable>
              );
            })}
            {/* dnd-kit's default drop animation eases the ghost to the dragged row's
                CURRENT DOM rect — but a cross-category (or reordered) drop only updates
                categoryId/order in the store, so the row's rect doesn't reflect its new
                slot until the next render. That stale-rect animation is the "snaps back
                to its former position, then the category blinks into place" glitch —
                disabling it makes the ghost simply vanish, and the real re-render lands
                in one clean step. */}
            <DragOverlay dropAnimation={null}>{draggingItem !== null && <DragPreview name={draggingItem.name} />}</DragOverlay>
          </DndContext>
        </SortableContext>
      </DndContext>
      {canEdit && <AddRow label="Add a new category" onAdd={onAddCategory} variant="dashed" />}
    </>
  );
};
